import logger from '@salesforce/retail-react-app/app/utils/logger-instance';

import {
  cartItemsCacheKey,
  getFullOrderTotal,
  PAYMENT_AMOUNT_TYPE,
  resolveCheckoutPaymentAmount,
} from '@amblique/king-living/app/pages/checkout/util/payment-amount-utils';
import {
  PAYPAL_APPROVAL_MAX_MS,
  PAYPAL_CHECKOUT_ROUTES,
  PAYPAL_CHECKOUT_STATUS,
  PAYPAL_ERROR_CODE_PATTERN,
  PAYPAL_PAYMENT_METHOD_ID,
  PAYPAL_PI_ATTR_ORDER_ID,
  PAYPAL_SESSION_KEYS,
  PAYPAL_TOKEN_PATTERN,
  PAYPAL_URL_QUERY,
} from '../paypal-constants';

const PAYPAL_AMOUNT_TOLERANCE = 0.005;

// ─── Session markers ────────────────────────────────────────────────────────

/**
 * @param {string} basketId
 * @param {Object} [checkoutSnapshot] Cart state at PayPal redirect — compared on return.
 */
export function markPayPalFlowStarted(basketId, checkoutSnapshot) {
  if (typeof window === 'undefined' || !basketId) {
    return;
  }

  try {
    sessionStorage.setItem(PAYPAL_SESSION_KEYS.FLOW_STARTED_AT, String(Date.now()));
    sessionStorage.setItem(PAYPAL_SESSION_KEYS.FLOW_BASKET_ID, basketId);

    if (checkoutSnapshot) {
      sessionStorage.setItem(
        PAYPAL_SESSION_KEYS.FLOW_SNAPSHOT,
        JSON.stringify(checkoutSnapshot)
      );
    }
  } catch {
    // ignore quota errors
  }
}

function getPayPalCheckoutSnapshot() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(PAYPAL_SESSION_KEYS.FLOW_SNAPSHOT);

    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * @returns {{ basketId: string|null, startedAt: number|null }}
 */
export function getPayPalFlowSessionState() {
  if (typeof window === 'undefined') {
    return {basketId: null, startedAt: null};
  }

  try {
    const basketId = sessionStorage.getItem(PAYPAL_SESSION_KEYS.FLOW_BASKET_ID);
    const startedAt = Number(sessionStorage.getItem(PAYPAL_SESSION_KEYS.FLOW_STARTED_AT));

    return {
      basketId: basketId || null,
      startedAt: Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null,
    };
  } catch {
    return {basketId: null, startedAt: null};
  }
}

/**
 * @param {string} basketId
 * @returns {boolean}
 */
export function isPayPalApprovalStale(basketId) {
  if (typeof window === 'undefined' || !basketId) {
    return false;
  }

  const {basketId: storedBasketId, startedAt} = getPayPalFlowSessionState();

  if (storedBasketId !== basketId || !startedAt) {
    return false;
  }

  return Date.now() - startedAt > PAYPAL_APPROVAL_MAX_MS;
}

/**
 * @param {string} [basketId]
 */
export function clearPayPalSession(basketId) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (!basketId || sessionStorage.getItem(PAYPAL_SESSION_KEYS.FLOW_BASKET_ID) === basketId) {
      sessionStorage.removeItem(PAYPAL_SESSION_KEYS.FLOW_STARTED_AT);
      sessionStorage.removeItem(PAYPAL_SESSION_KEYS.FLOW_BASKET_ID);
      sessionStorage.removeItem(PAYPAL_SESSION_KEYS.FLOW_SNAPSHOT);
    }
  } catch {
    // ignore
  }
}

// ─── Basket flow helpers ─────────────────────────────────────────────────────

/**
 * True when the basket still carries PayPal redirect / prepare metadata.
 *
 * @param {Object|null|undefined} basket
 * @returns {boolean}
 */
export function basketHasPendingPayPalFlow(basket) {
  if (!basket) {
    return false;
  }

  if (basket[PAYPAL_PI_ATTR_ORDER_ID]) {
    return true;
  }

  return (basket.paymentInstruments || []).some((pi) => (
    pi.paymentMethodId === PAYPAL_PAYMENT_METHOD_ID &&
    Boolean(pi[PAYPAL_PI_ATTR_ORDER_ID])
  ));
}

/**
 * PayPal order id staged on basket during /server/paypal/create (before redirect).
 *
 * @param {Object|null|undefined} basket
 * @returns {string|null}
 */
export function getStagedPayPalOrderId(basket) {
  if (!basket) {
    return null;
  }

  const basketLevelId = basket[PAYPAL_PI_ATTR_ORDER_ID];
  if (basketLevelId) {
    return String(basketLevelId).trim() || null;
  }

  const instrument = (basket.paymentInstruments || []).find(
    (pi) => pi.paymentMethodId === PAYPAL_PAYMENT_METHOD_ID && pi[PAYPAL_PI_ATTR_ORDER_ID]
  );

  return instrument?.[PAYPAL_PI_ATTR_ORDER_ID]
    ? String(instrument[PAYPAL_PI_ATTR_ORDER_ID]).trim()
    : null;
}

/** User-facing errors before basket mutation — shopper can retry checkout. */
export const PAYPAL_RETURN_VALIDATION_ERROR = Object.freeze({
  APPROVAL_EXPIRED: 'PayPal approval window exceeded',
  MISSING_CONTEXT: 'PayPal return is missing required checkout context.',
  SESSION_MISMATCH: 'PayPal return does not match this checkout session.',
  TOKEN_MISMATCH: 'PayPal return token does not match this cart.',
  BASKET_CHANGED:
    'Your cart was updated while paying with PayPal. Please review your order and try again.',
});

const PAYPAL_RETURN_VALIDATION_MESSAGES = new Set(Object.values(PAYPAL_RETURN_VALIDATION_ERROR));

/**
 * @param {string|null|undefined} message
 * @returns {boolean}
 */
export function isPayPalReturnValidationError(message) {
  if (typeof message !== 'string') {
    return false;
  }

  return PAYPAL_RETURN_VALIDATION_MESSAGES.has(message.trim());
}

/**
 * Block PayPal return handling when session, basket, or token do not match the active flow.
 *
 * @param {Object|null|undefined} basket
 * @param {string} token PayPal order id from return URL
 * @returns {string|null} User-facing error message, or null when return may proceed
 */
export function getPayPalReturnBlockingError(basket, token) {
  const basketId = basket?.basketId;
  const normalizedToken = token ? String(token).trim() : '';

  if (!basketId || !normalizedToken) {
    return PAYPAL_RETURN_VALIDATION_ERROR.MISSING_CONTEXT;
  }

  const {basketId: sessionBasketId, startedAt} = getPayPalFlowSessionState();

  if (!sessionBasketId || !startedAt || sessionBasketId !== basketId) {
    return PAYPAL_RETURN_VALIDATION_ERROR.SESSION_MISMATCH;
  }

  if (isPayPalApprovalStale(basketId)) {
    return PAYPAL_RETURN_VALIDATION_ERROR.APPROVAL_EXPIRED;
  }

  const stagedOrderId = getStagedPayPalOrderId(basket);

  if (!stagedOrderId || stagedOrderId !== normalizedToken) {
    return PAYPAL_RETURN_VALIDATION_ERROR.TOKEN_MISMATCH;
  }

  return null;
}

const amountsNearlyEqual = (left, right) => Number.isFinite(left) && Number.isFinite(right) &&
  Math.abs(left - right) < PAYPAL_AMOUNT_TOLERANCE;

/** Compare snapshot from PayPal redirect with the refetched basket. */
export function getPayPalCheckoutSnapshotMismatch(basket, options = {}) {
  if (!basket?.basketId) {
    return null;
  }

  const storedSnapshot = getPayPalCheckoutSnapshot();
  const {basketId: sessionBasketId, startedAt} = getPayPalFlowSessionState();

  if (!storedSnapshot) {
    if (sessionBasketId === basket.basketId && startedAt) {
      logPayPalClientError('return/snapshot-missing', {basketId: basket.basketId});

      return PAYPAL_RETURN_VALIDATION_ERROR.BASKET_CHANGED;
    }

    return null;
  }

  const {
    productsByProductId = null,
    depositRate = null,
    paymentAmountType = basket.c_paymentAmountType,
    inventoryIds = null,
  } = options;

  const currentPaymentAmount = resolveCheckoutPaymentAmount(
    {...basket, c_paymentAmountType: paymentAmountType},
    productsByProductId,
    paymentAmountType === PAYMENT_AMOUNT_TYPE.DEPOSIT ? depositRate : null,
    // KL-1044 — match the storefront/server pay-now math: in-stock items are
    // deposit-ineligible (paid in full), so the recomputed amount reconciles
    // with the snapshot captured at prepare time instead of a false mismatch.
    inventoryIds
  );

  const isUnchanged =
    storedSnapshot.cartSig === cartItemsCacheKey(basket.productItems) &&
    storedSnapshot.paymentAmountType === paymentAmountType &&
    amountsNearlyEqual(storedSnapshot.paymentAmount, currentPaymentAmount) &&
    amountsNearlyEqual(storedSnapshot.orderTotal, getFullOrderTotal(basket));

  if (isUnchanged) {
    return null;
  }

  logPayPalClientError('return/snapshot-mismatch', {basketId: basket.basketId});

  return PAYPAL_RETURN_VALIDATION_ERROR.BASKET_CHANGED;
}

// ─── URL sanitizers ──────────────────────────────────────────────────────────

export function sanitizePayPalToken(token) {
  if (typeof token !== 'string') {
    return null;
  }

  const trimmed = token.trim();

  return !trimmed || trimmed.length > 127 || !PAYPAL_TOKEN_PATTERN.test(trimmed) ? null : trimmed;
}

export function sanitizePayPalCheckoutStatus(status) {
  return status === PAYPAL_CHECKOUT_STATUS.CANCELLED ||
    status === PAYPAL_CHECKOUT_STATUS.ERROR ?
    status :
    null;
}

export function sanitizePayPalErrorCode(code) {
  if (typeof code !== 'string') {
    return null;
  }

  const trimmed = code.trim();

  return !trimmed || !PAYPAL_ERROR_CODE_PATTERN.test(trimmed) ? null : trimmed;
}

function trimMessage(message) {
  return typeof message === 'string' && message.trim() ? message.trim().slice(0, 500) : null;
}

/**
 * @param {string|null|undefined} basketId
 * @param {{ status: string, code?: string|null, message?: string|null }} payload
 */
export function stashPayPalCheckoutStatus(basketId, {status, code, message}) {
  if (typeof window === 'undefined' || !sanitizePayPalCheckoutStatus(status)) {
    return;
  }

  try {
    localStorage.setItem(
      PAYPAL_SESSION_KEYS.CHECKOUT_STATUS,
      JSON.stringify({
        basketId: basketId ?? null,
        code: sanitizePayPalErrorCode(code),
        message: trimMessage(message),
        status,
      })
    );
  } catch {
    // ignore
  }
}

/**
 * @param {string} search
 * @param {string|null|undefined} basketId
 * @returns {boolean}
 */
export function stashPayPalStatusFromSearch(search, basketId) {
  const params = new URLSearchParams(search);
  const status = sanitizePayPalCheckoutStatus(params.get(PAYPAL_URL_QUERY.STATUS));

  if (!status) {
    return false;
  }

  stashPayPalCheckoutStatus(basketId, {
    code: sanitizePayPalErrorCode(params.get(PAYPAL_URL_QUERY.CODE)),
    message: null,
    status,
  });

  return true;
}

/**
 * @param {string} search
 * @param {string} basketId
 * @returns {{ status: string|null, code: string|null, message: string|null }}
 */
export function resolvePayPalReturnContext(search, basketId) {
  const params = new URLSearchParams(search);
  const statusFromQuery = sanitizePayPalCheckoutStatus(params.get(PAYPAL_URL_QUERY.STATUS));

  if (statusFromQuery) {
    return {
      code: sanitizePayPalErrorCode(params.get(PAYPAL_URL_QUERY.CODE)),
      message: null,
      status: statusFromQuery,
    };
  }

  if (typeof window === 'undefined') {
    return {code: null, message: null, status: null};
  }

  try {
    const raw = localStorage.getItem(PAYPAL_SESSION_KEYS.CHECKOUT_STATUS);

    if (!raw) {
      return {code: null, message: null, status: null};
    }

    localStorage.removeItem(PAYPAL_SESSION_KEYS.CHECKOUT_STATUS);
    const pending = JSON.parse(raw);

    if (!pending?.status || (pending.basketId && pending.basketId !== basketId)) {
      return {code: null, message: null, status: null};
    }

    return {
      code: sanitizePayPalErrorCode(pending.code),
      message: trimMessage(pending.message),
      status: sanitizePayPalCheckoutStatus(pending.status),
    };
  } catch {
    try {
      localStorage.removeItem(PAYPAL_SESSION_KEYS.CHECKOUT_STATUS);
    } catch {
      // ignore
    }

    return {code: null, message: null, status: null};
  }
}

/**
 * Stash PayPal error and navigate to checkout banner.
 *
 * @param {Function} navigate
 * @param {string} basketId
 * @param {{ code?: string, message?: string }} error
 */
export function navigatePayPalCheckoutError(navigate, basketId, {code, message}) {
  stashPayPalCheckoutStatus(basketId, {
    code,
    message,
    status: PAYPAL_CHECKOUT_STATUS.ERROR,
  });
  const params = new URLSearchParams({[PAYPAL_URL_QUERY.STATUS]: PAYPAL_CHECKOUT_STATUS.ERROR});

  if (code) {
    params.set(PAYPAL_URL_QUERY.CODE, code);
  }

  navigate(`${PAYPAL_CHECKOUT_ROUTES.CHECKOUT}?${params.toString()}`);
}

// ─── Logger ──────────────────────────────────────────────────────────────────

function serializeOriginError(error) {
  if (error == null) {
    return null;
  }

  if (typeof error === 'string') {
    return {message: error};
  }

  return {
    name: error.name,
    message: error.message,
    code: error.code,
  };
}

/**
 * PayPal client failures — MRT namespace `paypal` (matches SFCC BM custom log).
 *
 * @param {string} context
 * @param {Record<string, unknown>} [meta]
 */
export function logPayPalClientError(context, meta = {}) {
  const {originError, error, ...rest} = meta;

  logger.error(`[PayPal] ${context}`, {
    namespace: 'paypal',
    additionalProperties: {
      ...rest,
      originError: originError ?? serializeOriginError(error),
    },
  });
}
